import { useCallback, useEffect, useMemo, useState } from 'react';

interface AssetWithId {
  getId: () => string;
}

export interface UseAssetSelectionOptions<T extends AssetWithId = AssetWithId> {
  enableMultiSelect?: boolean;
  /** Assets currently visible under the active filters/sort */
  visibleAssets?: T[];
}

export interface UseAssetSelectionResult<T extends AssetWithId = AssetWithId> {
  selectedAssetIds: string[];
  selectedCount: number;
  visibleSelectedCount: number;
  hiddenSelectedCount: number;
  selectedAssets: T[];
  isSelected: (assetId: string) => boolean;
  toggleSelection: (assetId: string) => void;
  addToSelection: (assetIds: string[]) => void;
  clearSelection: () => void;
  setSelection: (assetIds: string[]) => void;
}

export const useAssetSelection = <T extends AssetWithId>({
  enableMultiSelect = true,
  visibleAssets = [],
}: UseAssetSelectionOptions<T> = {}): UseAssetSelectionResult<T> => {
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
  const [assetCache, setAssetCache] = useState<Record<string, T>>({});

  useEffect(() => {
    if (visibleAssets.length === 0) return;

    setAssetCache((prev) => {
      const next = { ...prev };
      let changed = false;
      visibleAssets.forEach((asset) => {
        if (prev[asset.getId()] !== asset) {
          next[asset.getId()] = asset;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [visibleAssets]);

  useEffect(() => {
    if (enableMultiSelect || selectedAssetIds.length <= 1) return;

    setSelectedAssetIds([selectedAssetIds[selectedAssetIds.length - 1]]);
  }, [enableMultiSelect, selectedAssetIds]);

  const toggleSelection = useCallback((assetId: string) => {
    setSelectedAssetIds((prev) => {
      if (enableMultiSelect) {
        return prev.includes(assetId)
          ? prev.filter((id) => id !== assetId)
          : [...prev, assetId];
      }

      return prev.includes(assetId) ? [] : [assetId];
    });
  }, [enableMultiSelect]);

  const clearSelection = useCallback(() => {
    setSelectedAssetIds([]);
  }, []);

  const addToSelection = useCallback((assetIds: string[]) => {
    if (assetIds.length === 0) return;

    setSelectedAssetIds((prev) => {
      if (!enableMultiSelect) {
        return [assetIds[assetIds.length - 1]];
      }

      const merged = new Set(prev);
      assetIds.forEach((id) => merged.add(id));
      return Array.from(merged);
    });
  }, [enableMultiSelect]);

  const setSelection = useCallback((assetIds: string[]) => {
    setSelectedAssetIds(assetIds);
  }, []);

  const isSelected = useCallback((assetId: string) => {
    return selectedAssetIds.includes(assetId);
  }, [selectedAssetIds]);

  const selectedCount = useMemo(() => selectedAssetIds.length, [selectedAssetIds]);

  const selectedAssets = useMemo(() => {
    return selectedAssetIds
      .map((id) => assetCache[id])
      .filter((asset): asset is T => Boolean(asset));
  }, [selectedAssetIds, assetCache]);

  const visibleSelectedCount = useMemo(() => {
    const visibleIds = new Set(visibleAssets.map((asset) => asset.getId()));
    return selectedAssetIds.filter((id) => visibleIds.has(id)).length;
  }, [visibleAssets, selectedAssetIds]);

  const hiddenSelectedCount = useMemo(() => {
    return Math.max(0, selectedCount - visibleSelectedCount);
  }, [selectedCount, visibleSelectedCount]);

  return {
    selectedAssetIds,
    selectedCount,
    visibleSelectedCount,
    hiddenSelectedCount,
    selectedAssets,
    isSelected,
    toggleSelection,
    addToSelection,
    clearSelection,
    setSelection,
  };
};